/**
 * Script: DonationPickupService.js
 * Caminho: src/services/DonationPickupService.js
 * Descricao: Service frontend para operacoes e validacoes de formulario
 * publico de Abholspenden.
 *
 * Relacao com o Projeto:
 *  - Centraliza regras puras do formulario publico /spenden.
 *  - Chama a Cloud Function publica submitPublicSpendenForm.
 *
 * Entradas (Parametros):
 *  - `form` (Object) - Estado do formulario publico de Abholspenden.
 *  - Payloads futuros de triagem, agendamento e coleta.
 *
 * Saidas:
 *  - Estado inicial, validacao, payload sanitizado e Promises futuras.
 *
 * Exports:
 *  - `DONATION_PICKUP_FORM_LIMITS` - Limites de campos e fotos.
 *  - `createDonationPickupFormState()` - Cria estado inicial.
 *  - `createDonationPickupItem()` - Cria item do formulario.
 *  - `buildDonationPickupLocationValue()` - Cria value do dropdown.
 *  - `resolveDonationPickupLocationValue()` - Resolve localidade atendida.
 *  - `validatePublicDonationPickupForm()` - Valida formulario publico.
 *  - `buildPublicDonationPickupPayload()` - Monta payload sem binarios.
 *  - `submitPublicDonationPickupForm()` - Contrato de envio publico.
 *  - Demais exports mantem contratos futuros dos cards internos.
 *
 * Dependencias:
 *  - Utilitarios internos: DonationPickupContracts.
 *  - Componentes Vue: SpendenFormularView.vue e cards futuros.
 *  - Backend (Cloud Functions/API): submitPublicSpendenForm.
 *  - Bibliotecas externas (NPM): Firebase Web SDK modular.
 */

import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { db, functions } from "@/firebaseConfig";
import {
  DONATION_PICKUP_ITEM_PICKUP_STATUSES,
  DONATION_PICKUP_ITEM_REVIEW_STATUSES,
  DONATION_PICKUP_PROTOCOL_STATUSES,
  findDonationPickupLocation,
  findInternalDonationPickupLocation,
  DONATION_PICKUP_STATIC_CATEGORIES,
} from "./DonationPickupContracts";
import { ensureDonationPickupAnonymousAuth } from "./DonationPickupUploadService";

export const DONATION_PICKUP_FORM_LIMITS = Object.freeze({
  ITEM_TITLE_MAX: 60,
  ITEM_DESCRIPTION_MAX: 120,
  PHOTOS_MIN: 1,
  PHOTOS_MAX: 3,
  FIRST_NAME_MAX: 50,
  LAST_NAME_MAX: 50,
  PHONE_MAX: 20,
  EMAIL_MAX: 100,
  STREET_MAX: 100,
  HOUSE_NUMBER_MAX: 10,
  FLOOR_MAX: 20,
});

const REQUIRED_FIELDS = Object.freeze([
  "firstName",
  "lastName",
  "phone",
  "email",
  "street",
  "houseNumber",
  "selectedLocation",
]);

const VALID_PICKUP_RESULTS = new Set([
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.PENDING_PICKUP,
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.COLLECTED,
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.DISCARDED_AT_PICKUP,
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.NOT_COLLECTED_NEW_PROTOCOL_REQUIRED,
]);

const PICKUP_RESULTS_REQUIRING_REASON = new Set([
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.DISCARDED_AT_PICKUP,
  DONATION_PICKUP_ITEM_PICKUP_STATUSES.NOT_COLLECTED_NEW_PROTOCOL_REQUIRED,
]);

const VALIDATION_KEYS = Object.freeze({
  REQUIRED: "vitrine.spenden.validation.required",
  INVALID_EMAIL: "vitrine.spenden.validation.invalidEmail",
  INVALID_PHONE: "vitrine.spenden.validation.invalidPhone",
  INVALID_HOUSE_NUMBER: "vitrine.spenden.validation.invalidHouseNumber",
  UNSUPPORTED_LOCATION: "vitrine.spenden.validation.unsupportedLocation",
  PRIVACY_REQUIRED: "vitrine.spenden.validation.privacyRequired",
  OWNERSHIP_REQUIRED: "vitrine.spenden.validation.ownershipRequired",
  ITEM_REQUIRED: "vitrine.spenden.validation.itemRequired",
  TITLE_TOO_LONG: "vitrine.spenden.validation.titleTooLong",
  DESCRIPTION_TOO_LONG: "vitrine.spenden.validation.descriptionTooLong",
  PHOTOS_MIN: "vitrine.spenden.validation.photosMin",
  PHOTOS_MAX: "vitrine.spenden.validation.photosMax",
  UPLOADS_PENDING: "vitrine.spenden.validation.uploadsPending",
});

function trimValue(value) {
  return String(value || "").trim();
}

function normalizeEmail(value) {
  return trimValue(value).toLowerCase();
}

function isValidEmail(value) {
  return value.includes("@");
}

function isValidPhone(value) {
  return /^[+0-9][0-9\s()./-]{5,}$/.test(trimValue(value));
}

function isValidHouseNumber(value) {
  return /^\d+$/.test(trimValue(value));
}

function buildItemId(index) {
  return `item_${String(index).padStart(3, "0")}`;
}

export function createDonationPickupItem(index = 1) {
  return {
    id: buildItemId(index),
    title: "",
    description: "",
    photos: [],
  };
}

export function createDonationPickupFormState() {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    street: "",
    houseNumber: "",
    floor: "",
    selectedLocation: "",
    privacyAccepted: false,
    ownershipConfirmed: false,
    items: [createDonationPickupItem(1)],
  };
}

export function buildDonationPickupLocationValue(location) {
  if (!location) return "";
  return `${location.postalCode}|${location.city}`;
}

export function resolveDonationPickupLocationValue(value) {
  const [postalCode, city] = trimValue(value).split("|");
  if (!postalCode || !city) return null;
  return findDonationPickupLocation(postalCode, city);
}

function validateRequiredFields(form, fieldErrors) {
  REQUIRED_FIELDS.forEach((fieldName) => {
    if (!trimValue(form[fieldName])) {
      fieldErrors[fieldName] = VALIDATION_KEYS.REQUIRED;
    }
  });
}

function validateCustomerFields(form, fieldErrors) {
  validateRequiredFields(form, fieldErrors);

  if (trimValue(form.email) && !isValidEmail(form.email)) {
    fieldErrors.email = VALIDATION_KEYS.INVALID_EMAIL;
  }
  if (trimValue(form.phone) && !isValidPhone(form.phone)) {
    fieldErrors.phone = VALIDATION_KEYS.INVALID_PHONE;
  }
  if (trimValue(form.houseNumber) && !isValidHouseNumber(form.houseNumber)) {
    fieldErrors.houseNumber = VALIDATION_KEYS.INVALID_HOUSE_NUMBER;
  }
  if (
    trimValue(form.selectedLocation) &&
    !resolveDonationPickupLocationValue(form.selectedLocation)
  ) {
    fieldErrors.selectedLocation = VALIDATION_KEYS.UNSUPPORTED_LOCATION;
  }
  if (!form.privacyAccepted) {
    fieldErrors.privacyAccepted = VALIDATION_KEYS.PRIVACY_REQUIRED;
  }
  if (!form.ownershipConfirmed) {
    fieldErrors.ownershipConfirmed = VALIDATION_KEYS.OWNERSHIP_REQUIRED;
  }
}

function validateItems(form) {
  const itemErrors = {};
  const items = Array.isArray(form.items) ? form.items : [];

  if (!items.length) {
    return {
      _form: VALIDATION_KEYS.ITEM_REQUIRED,
    };
  }

  items.forEach((item) => {
    const errors = {};
    const title = trimValue(item.title);
    const description = trimValue(item.description);
    const photos = Array.isArray(item.photos) ? item.photos : [];
    const photoCount = photos.length;
    const uploadedPhotoCount = photos.filter(
      (photo) => photo?.storagePath && photo?.uploadStatus === "uploaded"
    ).length;

    if (!title) {
      errors.title = VALIDATION_KEYS.REQUIRED;
    } else if (title.length > DONATION_PICKUP_FORM_LIMITS.ITEM_TITLE_MAX) {
      errors.title = VALIDATION_KEYS.TITLE_TOO_LONG;
    }

    if (description.length > DONATION_PICKUP_FORM_LIMITS.ITEM_DESCRIPTION_MAX) {
      errors.description = VALIDATION_KEYS.DESCRIPTION_TOO_LONG;
    }

    if (photoCount < DONATION_PICKUP_FORM_LIMITS.PHOTOS_MIN) {
      errors.photos = VALIDATION_KEYS.PHOTOS_MIN;
    } else if (photoCount > DONATION_PICKUP_FORM_LIMITS.PHOTOS_MAX) {
      errors.photos = VALIDATION_KEYS.PHOTOS_MAX;
    } else if (uploadedPhotoCount !== photoCount) {
      errors.photos = VALIDATION_KEYS.UPLOADS_PENDING;
    }

    if (Object.keys(errors).length) {
      itemErrors[item.id] = errors;
    }
  });

  return itemErrors;
}

export function validatePublicDonationPickupForm(form) {
  const safeForm = form || {};
  const fieldErrors = {};
  validateCustomerFields(safeForm, fieldErrors);
  const itemErrors = validateItems(safeForm);

  return {
    valid: !Object.keys(fieldErrors).length && !Object.keys(itemErrors).length,
    fields: fieldErrors,
    items: itemErrors,
  };
}

export function buildPublicDonationPickupPayload(form, uploadSession = null) {
  const location = resolveDonationPickupLocationValue(form.selectedLocation);

  return {
    uploadSession: uploadSession
      ? {
          sessionId: trimValue(uploadSession.sessionId),
          authUid: trimValue(uploadSession.authUid),
        }
      : null,
    customer: {
      firstName: trimValue(form.firstName),
      lastName: trimValue(form.lastName),
      phone: trimValue(form.phone),
      email: normalizeEmail(form.email),
      address: {
        street: trimValue(form.street),
        houseNumber: trimValue(form.houseNumber),
        floor: trimValue(form.floor) || null,
        postalCode: location?.postalCode || null,
        city: location?.city || null,
        routeCode: location?.routeCode || null,
      },
    },
    consent: {
      privacyAccepted: Boolean(form.privacyAccepted),
      ownershipConfirmed: Boolean(form.ownershipConfirmed),
    },
    items: form.items.map((item) => ({
      id: item.id,
      title: trimValue(item.title),
      description: trimValue(item.description),
      photoCount: Array.isArray(item.photos) ? item.photos.length : 0,
      photoPaths: (Array.isArray(item.photos) ? item.photos : [])
        .map((photo) => trimValue(photo.storagePath))
        .filter(Boolean),
    })),
  };
}

export async function submitPublicDonationPickupForm(payload) {
  const callable = httpsCallable(functions, "submitPublicSpendenForm");
  const result = await callable(payload);
  return result.data;
}

export async function submitOperatorDonationPickupForm(payload) {
  const callable = httpsCallable(functions, "submitOperatorSpendenForm");
  const result = await callable(payload);
  return result.data;
}

export function normalizeDonationPickupProtocol(value) {
  return trimValue(value).toUpperCase().replace(/-/g, "");
}

export function formatDonationPickupProtocolInput(value) {
  const normalized = normalizeDonationPickupProtocol(value);
  if (!normalized) return "";
  if (!normalized.startsWith("P")) return "";

  const digits = normalized.slice(1).replace(/\D/g, "").slice(0, 9);
  if (!digits) return "P";
  if (digits.length <= 6) return `P${digits}`;
  return `P${digits.slice(0, 6)}-${digits.slice(6)}`;
}

export function isValidDonationPickupProtocol(value) {
  return /^P\d{9}$/.test(normalizeDonationPickupProtocol(value));
}

/**
 * Extrai a data de criacao a partir do numero do protocolo (PYYMMDDNNN).
 * @param {string} value Protocolo.
 * @return {Date|null} Data de criacao (meia-noite local) ou null se invalido.
 */
export function parseDonationPickupProtocolDate(value) {
  const normalized = normalizeDonationPickupProtocol(value);
  if (!isValidDonationPickupProtocol(normalized)) return null;

  const year = 2000 + Number(normalized.slice(1, 3));
  const month = Number(normalized.slice(3, 5));
  const day = Number(normalized.slice(5, 7));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  // Rejeita datas invalidas como 31/02 (o Date "rola" para marco).
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/**
 * Monta as chaves mensais (MMYY) do intervalo, inclusive nas duas pontas.
 * @param {Date} from Data inicial.
 * @param {Date} to Data final.
 * @return {Array<string>} Chaves mensais no formato MMYY.
 */
export function buildDonationPickupMonthKeys(from, to = new Date()) {
  const keys = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);

  while (cursor <= end) {
    const mm = String(cursor.getMonth() + 1).padStart(2, "0");
    const yy = String(cursor.getFullYear()).slice(-2);
    keys.push(`${mm}${yy}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return keys;
}

/**
 * Carrega os protocolos criados nos ultimos N meses, opcionalmente filtrando
 * por status. Consulta apenas as subcolecoes mensais da janela, evitando o
 * collectionGroup com limite arbitrario sobre todo o historico.
 * @param {Object} params Parametros da consulta.
 * @param {number} [params.months] Tamanho da janela em meses.
 * @param {Date} [params.now] Data de referencia (facilita testes).
 * @param {string|Array<string>} [params.status] Status ou lista de status.
 * @return {Promise<Array<Object>>} Protocolos da janela.
 */
export async function loadRecentDonationPickupProtocols({
  months = 3,
  now = new Date(),
  status,
} = {}) {
  const { collection, getDocs, query, where } = await import(
    "firebase/firestore"
  );
  const { db } = await import("@/firebaseConfig");

  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cutoff.setMonth(cutoff.getMonth() - months);

  const monthKeys = buildDonationPickupMonthKeys(cutoff, now);
  const snapshots = await Promise.all(
    monthKeys.map((monthKey) => {
      const col = collection(db, "spenden", monthKey, "protocols");
      if (!status) return getDocs(col);
      if (Array.isArray(status)) {
        return getDocs(query(col, where("status", "in", status)));
      }
      return getDocs(query(col, where("status", "==", status)));
    })
  );

  const protocols = [];
  snapshots.forEach((snapshot, index) => {
    const monthKey = monthKeys[index];
    snapshot.docs.forEach((docSnap) => {
      const data = { id: docSnap.id, monthKey, ...docSnap.data() };
      const createdAt = parseDonationPickupProtocolDate(
        data.protocol || docSnap.id
      );
      // Protocolo com numero inesperado nao e escondido do operador.
      if (!createdAt || createdAt >= cutoff) protocols.push(data);
    });
  });

  return protocols;
}

/**
 * Consulta publica controlada do status de um protocolo.
 * @param {string} protocol Protocolo informado pelo cliente.
 * @return {Promise<Object>} Status publico do protocolo.
 */
export async function lookupDonationPickupProtocolStatus(protocol) {
  const normalizedProtocol = normalizeDonationPickupProtocol(protocol);
  if (!isValidDonationPickupProtocol(normalizedProtocol)) {
    throw new Error("invalid-donation-pickup-protocol");
  }

  await ensureDonationPickupAnonymousAuth();
  const callable = httpsCallable(functions, "lookupPublicSpendenStatus");
  const result = await callable({ protocol: normalizedProtocol });
  return result.data;
}

/**
 * Carrega protocolos com filtros especificos para os cards e dashboard.
 * @param {Object} params Filtros de consulta.
 * @param {string|Array<string>} [params.status] Status do protocolo (opcional).
 * @param {number} [params.limitCount] Limite de documentos.
 * @return {Promise<Array<Object>>} Protocolos encontrados.
 */
export async function loadDonationPickupProtocols({
  status,
  limitCount = 500,
} = {}) {
  const { collectionGroup, query, where, getDocs, limit } = await import(
    "firebase/firestore"
  );
  const { db } = await import("@/firebaseConfig");

  let q;
  const colGroup = collectionGroup(db, "protocols");

  if (!status) {
    q = query(colGroup, limit(limitCount));
  } else if (Array.isArray(status)) {
    q = query(colGroup, where("status", "in", status), limit(limitCount));
  } else {
    q = query(colGroup, where("status", "==", status), limit(limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    monthKey: doc.ref.parent.parent?.id || null,
    ...doc.data(),
  }));
}

/**
 * Carrega dados sensiveis do cliente para triagem.
 * @param {string} monthKey Chave mensal.
 * @param {string} protocol Protocolo.
 * @return {Promise<Object|null>} Dados do cliente.
 */
export async function loadDonationPickupCustomerData(monthKey, protocol) {
  const { doc, getDoc } = await import("firebase/firestore");
  const { db } = await import("@/firebaseConfig");
  const ref = doc(db, "spendenKunden", monthKey, "customerData", protocol);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/**
 * Classifica um item individual via Cloud Function.
 * @param {Object} params Dados da classificacao.
 * @return {Promise<Object>} Resultado.
 */
export async function reviewDonationPickupItem(params) {
  const callable = httpsCallable(functions, "reviewDonationPickupItem");
  const result = await callable(params);
  return result.data;
}

/**
 * Atribui resultado final da triagem via Cloud Function.
 * @param {Object} params Dados do resultado.
 * @return {Promise<Object>} Resultado.
 */
export async function assignDonationPickupReviewResult(params) {
  const callable = httpsCallable(functions, "assignDonationPickupReviewResult");
  const result = await callable(params);
  return result.data;
}

/**
 * Agenda coleta para protocolo via Cloud Function.
 * @param {Object} params Dados do agendamento.
 * @return {Promise<Object>} Resultado.
 */
export async function scheduleDonationPickup(params) {
  const callable = httpsCallable(functions, "scheduleDonationPickup");
  const result = await callable(params);
  return result.data;
}

/**
 * Registra tentativa de contato no historico do protocolo.
 * @param {string} monthKey Chave mensal.
 * @param {string} protocol Protocolo.
 * @param {Object} attempt Dados da tentativa.
 * @return {Promise<void>}
 */
export async function registerDonationPickupContactAttempt(
  monthKey,
  protocol,
  attempt
) {
  const { doc, updateDoc, arrayUnion, serverTimestamp } = await import(
    "firebase/firestore"
  );
  const { db } = await import("@/firebaseConfig");
  const ref = doc(db, "spenden", monthKey, "protocols", protocol);
  const contactAttempt = {
    method: trimValue(attempt.method),
    note: trimValue(attempt.note),
    at: new Date().toISOString(),
  };

  await updateDoc(ref, {
    "schedule.contactAttempts": arrayUnion(contactAttempt),
    "audit.updatedAt": serverTimestamp(),
  });
  return { success: true, attempt: contactAttempt };
}

/**
 * Atualiza telefone e/ou endereco do cliente pela central.
 * @param {Object} params Dados de atualizacao.
 * @return {Promise<Object>} Resultado.
 */
export async function updateDonationPickupCustomerContact(params) {
  const { doc, updateDoc, serverTimestamp } = await import(
    "firebase/firestore"
  );
  const { db } = await import("@/firebaseConfig");
  const { monthKey, protocol, phone, address } = params || {};
  if (!monthKey || !protocol) {
    throw new Error("monthKey e protocol obrigatorios.");
  }

  const customerUpdates = {
    "audit.updatedAt": serverTimestamp(),
  };
  const protocolUpdates = {
    "audit.updatedAt": serverTimestamp(),
    "customerDataRetention.lastSensitiveDataUpdateAt": serverTimestamp(),
  };

  const normalizedPhone = trimValue(phone);
  if (normalizedPhone) {
    if (!isValidPhone(normalizedPhone)) {
      throw new Error("Telefone invalido.");
    }
    customerUpdates["customer.phone"] = normalizedPhone;
  }

  if (address) {
    const sanitizedAddress = {
      street: trimValue(address.street),
      houseNumber: trimValue(address.houseNumber),
      floor: trimValue(address.floor) || null,
      postalCode: trimValue(address.postalCode),
      city: trimValue(address.city),
      routeCode: trimValue(address.routeCode),
    };
    const location = findInternalDonationPickupLocation(
      sanitizedAddress.postalCode,
      sanitizedAddress.city,
      sanitizedAddress.routeCode
    );
    if (!location || location.routeCode !== sanitizedAddress.routeCode) {
      throw new Error("Localidade nao atendida.");
    }
    customerUpdates["customer.address"] = sanitizedAddress;
    protocolUpdates["route.code"] = location.routeCode;
    protocolUpdates["route.postalCode"] = location.postalCode;
    protocolUpdates["route.city"] = location.city;
    protocolUpdates["route.correctedByCentral"] = true;
  }

  const customerRef = doc(
    db,
    "spendenKunden",
    monthKey,
    "customerData",
    protocol
  );
  const protocolRef = doc(db, "spenden", monthKey, "protocols", protocol);
  await Promise.all([
    updateDoc(customerRef, customerUpdates),
    updateDoc(protocolRef, protocolUpdates),
  ]);

  return { success: true, protocol };
}
/**
 * Registra resultado de coleta de um item por escrita direta autenticada.
 * @param {Object} params Dados do resultado.
 * @return {Promise<Object>} Resultado.
 */
export async function recordDonationPickupItemResult(params) {
  const { runTransaction, doc, getDoc, serverTimestamp } = await import(
    "firebase/firestore"
  );
  const { auth, db } = await import("@/firebaseConfig");
  const {
    monthKey,
    protocol,
    itemId,
    pickupStatus,
    pickupReason,
    forceDriverWins,
  } = params || {};

  if (!auth.currentUser || auth.currentUser.isAnonymous) {
    throw new Error("Usuario interno autenticado obrigatorio.");
  }
  if (!monthKey || !protocol || !itemId) {
    throw new Error("monthKey, protocol e itemId obrigatorios.");
  }
  if (!VALID_PICKUP_RESULTS.has(pickupStatus)) {
    throw new Error("pickupStatus invalido.");
  }

  const requiresReason = PICKUP_RESULTS_REQUIRING_REASON.has(pickupStatus);
  const trimmedReason = requiresReason ? trimValue(pickupReason) : null;
  if (requiresReason && !trimmedReason) {
    throw new Error("Motivo obrigatorio para este resultado.");
  }

  const userRef = doc(db, "users", auth.currentUser.uid);
  const userSnap = await getDoc(userRef);
  const mitarbeiterId =
    userSnap.exists() && userSnap.data()?.mitarbeiterId
      ? userSnap.data().mitarbeiterId
      : sessionStorage.getItem("mitarbeiterId") || auth.currentUser.uid;

  const protocolRef = doc(db, "spenden", monthKey, "protocols", protocol);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(protocolRef);
    if (!snap.exists()) {
      throw new Error("Protocolo nao encontrado.");
    }

    const data = snap.data();
    if (
      !forceDriverWins &&
      data.status !== DONATION_PICKUP_PROTOCOL_STATUSES.SCHEDULED
    ) {
      throw new Error("Protocolo nao esta agendado.");
    }

    const items = Array.isArray(data.items) ? [...data.items] : [];
    const itemIdx = items.findIndex((item) => item.id === itemId);
    if (itemIdx < 0) {
      throw new Error("Item nao encontrado.");
    }

    const item = items[itemIdx];
    if (
      !forceDriverWins &&
      item.reviewStatus !== DONATION_PICKUP_ITEM_REVIEW_STATUSES.ACCEPTED
    ) {
      throw new Error("Item nao esta aceito.");
    }

    const now = new Date();
    items[itemIdx] = {
      ...item,
      pickupStatus,
      pickupReason: trimmedReason,
      pickupDecidedAt: now,
      pickupDecidedBy: mitarbeiterId,
      pickupHistory: [
        ...(item.pickupHistory || []),
        {
          from: item.pickupStatus || null,
          to: pickupStatus,
          reason: trimmedReason,
          at: now,
          by: mitarbeiterId,
        },
      ],
    };

    transaction.update(protocolRef, {
      items,
      "audit.updatedAt": serverTimestamp(),
      "audit.updatedBy": mitarbeiterId,
    });
  });

  return { success: true, itemId, pickupStatus };
}

/**
 * Conclui coleta do protocolo via Cloud Function.
 * @param {Object} params Dados de conclusao.
 * @return {Promise<Object>} Resultado.
 */
export async function completeDonationPickup(params) {
  const callable = httpsCallable(functions, "completeDonationPickup");
  const result = await callable(params);
  return result.data;
}

/**
 * Solicita reagendamento de protocolo inteiro via Cloud Function.
 * @param {Object} params Dados com motivo.
 * @return {Promise<Object>} Resultado.
 */
export async function markDonationPickupReschedulePending(params) {
  const callable = httpsCallable(
    functions,
    "markDonationPickupReschedulePending"
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Cancela protocolo com motivo via Cloud Function.
 * @param {Object} params Dados com motivo.
 * @return {Promise<Object>} Resultado.
 */
export async function cancelDonationPickupRequest(params) {
  const callable = httpsCallable(functions, "cancelDonationPickupRequest");
  const result = await callable(params);
  return result.data;
}

/**
 * Adiciona anotação de correção na ficha do cliente.
 * @param {string} monthKey
 * @param {string} protocol
 * @param {string} text
 * @param {string} employeeId
 */
export async function addDonationPickupCustomerNote(
  monthKey,
  protocol,
  text,
  employeeId
) {
  const { doc, updateDoc, arrayUnion, serverTimestamp } = await import(
    "firebase/firestore"
  );
  const { db } = await import("@/firebaseConfig");

  const ref = doc(db, "spendenKunden", monthKey, "customerData", protocol);
  const noteObj = {
    text: String(text || "")
      .trim()
      .slice(0, 120),
    at: new Date().toISOString(),
    by: employeeId || "",
  };

  await updateDoc(ref, {
    "customer.correctionNotes": arrayUnion(noteObj),
    "audit.updatedAt": serverTimestamp(),
  });

  return { success: true, note: noteObj };
}

/**
 * Consulta interna de operador administrativo com leitura direta do Firestore.
 * Evita o login anonimo e resolve o status de forma isolada e em tempo real.
 * @param {string} protocol Protocolo informado.
 * @return {Promise<Object>} Status do protocolo formatado.
 */
export async function lookupOperatorDonationPickupProtocolStatus(protocol) {
  const normalizedProtocol = normalizeDonationPickupProtocol(protocol);
  if (!isValidDonationPickupProtocol(normalizedProtocol)) {
    throw new Error("invalid-donation-pickup-protocol");
  }

  // 1. Deriva monthKey a partir de PYYMMDDNNN -> MMYY
  const yy = normalizedProtocol.slice(1, 3);
  const mm = normalizedProtocol.slice(3, 5);
  const monthKey = `${mm}${yy}`;

  // 2. Leitura direta do Firestore usando a instancia do operador
  const docRef = doc(db, "spenden", monthKey, "protocols", normalizedProtocol);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    throw new Error("not-found");
  }

  const protocolData = snap.data() || {};
  const status = protocolData.status || "";

  // 3. Resolve statusAt (data de atualizacao do status atual)
  const history = Array.isArray(protocolData.statusHistory)
    ? protocolData.statusHistory
    : [];
  const currentHistory = history.filter((h) => h && h.status === status);
  let statusAt = null;
  if (currentHistory.length) {
    const sorted = [...currentHistory].sort((a, b) => {
      const aTime =
        a.at && typeof a.at.toMillis === "function"
          ? a.at.toMillis()
          : a.at
          ? new Date(a.at).getTime()
          : 0;
      const bTime =
        b.at && typeof b.at.toMillis === "function"
          ? b.at.toMillis()
          : b.at
          ? new Date(b.at).getTime()
          : 0;
      return bTime - aTime;
    });
    const latest = sorted[0];
    if (latest && latest.at) {
      statusAt =
        typeof latest.at.toDate === "function"
          ? latest.at.toDate().toISOString()
          : new Date(latest.at).toISOString();
    }
  }
  if (!statusAt && protocolData.audit && protocolData.audit.updatedAt) {
    const updatedAt = protocolData.audit.updatedAt;
    statusAt =
      typeof updatedAt.toDate === "function"
        ? updatedAt.toDate().toISOString()
        : new Date(updatedAt).toISOString();
  }

  return {
    protocol: normalizedProtocol,
    status,
    statusAt,
    statusLabelKey: `abholspenden.schedule.lookup.status.${status}`,
    guidanceKey: `abholspenden.schedule.lookup.guidance.${status}`,
    scheduledDate:
      protocolData.schedule && protocolData.schedule.date
        ? protocolData.schedule.date
        : null,
    items: (Array.isArray(protocolData.items) ? protocolData.items : []).map(
      (item) => ({
        title: String(item?.title || "")
          .trim()
          .toLocaleUpperCase("de-DE"),
        reviewStatus: item?.reviewStatus || null,
        rejectionReason: item?.rejectionReason || null,
        pickupStatus: item?.pickupStatus || null,
        pickupReason: item?.pickupReason || null,
      })
    ),
    cancellationReason: protocolData.cancellation?.reason || null,
    rescheduleReason: protocolData.pickup?.rescheduleReason || null,
  };
}

export async function loadDonationPickupCategories() {
  return [...DONATION_PICKUP_STATIC_CATEGORIES];
}

export async function attachDonationPickupPhotos(payload) {
  const callable = httpsCallable(functions, "attachDonationPickupPhotos");
  const result = await callable(payload);
  return result.data;
}
